import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Signup.css";

// Get API URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

const Signup = () => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    gender: "prefer not to say",
  });

  const [profileImage, setProfileImage] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({
    length: true,
    uppercase: true,
    lowercase: true,
    number: true,
    special: true
  });
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "password") {
      // Validate password requirements
      validatePassword(value);
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validatePassword = (password) => {
    setPasswordErrors({
      length: password.length < 10,
      uppercase: !/[A-Z]/.test(password),
      lowercase: !/[a-z]/.test(password),
      number: !/\d/.test(password),
      special: !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setProfileImage(e.target.files[0]);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsLoading(true);

    // Check if password meets all requirements
    const hasPasswordErrors = Object.values(passwordErrors).some(error => error);
    if (hasPasswordErrors) {
      setError("Password does not meet all requirements");
      setIsLoading(false);
      return;
    }

    // Create FormData for file upload
    const formDataToSend = new FormData();
    formDataToSend.append("username", formData.username);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("password", formData.password);
    formDataToSend.append("gender", formData.gender);
    
    // Add profile picture if selected
    if (profileImage) {
      formDataToSend.append("profilePicture", profileImage);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/signup`, {
        method: "POST",
        body: formDataToSend,
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || "Signup failed");
      }
      
      // Show success message instead of redirecting immediately
      setSuccess(
        data.message || 
        "Account created successfully! Please check your email to verify your account."
      );
      
      // Clear the form
      setFormData({
        username: "",
        email: "",
        password: "",
        gender: "prefer not to say",
      });
      setProfileImage(null);
      
      // Optionally redirect to verification page after a delay
      setTimeout(() => {
        navigate("/verification-pending", { 
          state: { 
            email: formData.email,
            message: data.message
          } 
        });
      }, 3000);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to check if all password requirements are met
  const isPasswordValid = () => !Object.values(passwordErrors).some(error => error);

  return (
    <div className="signup">
      <h1>Create Account</h1>
      
      {error && <p className="error-message">{error}</p>}
      {success && <p className="success-message">{success}</p>}

      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <input
            type="text"
            name="username"
            placeholder="Username"
            required
            minLength="3"
            maxLength="30"
            value={formData.username}
            onChange={handleChange}
            disabled={isLoading}
          />
          <input
            type="email"
            name="email"
            placeholder="Email"
            required
            value={formData.email}
            onChange={handleChange}
            disabled={isLoading}
          />
          <div className="password-container">
            <div className="password-field">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Password"
                required
                value={formData.password}
                onChange={handleChange}
                className={isPasswordValid() ? "valid-password" : ""}
                disabled={isLoading}
              />
              <button 
                type="button" 
                className="password-toggle" 
                onClick={togglePasswordVisibility}
                tabIndex="-1"
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            <div className="password-requirements">
              <p className={passwordErrors.length ? "requirement" : "requirement met"}>
                ✓ At least 10 characters
              </p>
              <p className={passwordErrors.uppercase ? "requirement" : "requirement met"}>
                ✓ At least one uppercase letter
              </p>
              <p className={passwordErrors.lowercase ? "requirement" : "requirement met"}>
                ✓ At least one lowercase letter
              </p>
              <p className={passwordErrors.number ? "requirement" : "requirement met"}>
                ✓ At least one number
              </p>
              <p className={passwordErrors.special ? "requirement" : "requirement met"}>
                ✓ At least one special character
              </p>
            </div>
          </div>
        </div>

        <div className="input-group gender-group">
          <h3>Gender</h3>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="gender"
                value="male"
                checked={formData.gender === "male"}
                onChange={handleChange}
                disabled={isLoading}
              />
              Male
            </label>
            <label>
              <input
                type="radio"
                name="gender"
                value="female"
                checked={formData.gender === "female"}
                onChange={handleChange}
                disabled={isLoading}
              />
              Female
            </label>
            <label>
              <input
                type="radio"
                name="gender"
                value="other"
                checked={formData.gender === "other"}
                onChange={handleChange}
                disabled={isLoading}
              />
              Other
            </label>
            <label>
              <input
                type="radio"
                name="gender"
                value="prefer not to say"
                checked={formData.gender === "prefer not to say"}
                onChange={handleChange}
                disabled={isLoading}
              />
              Prefer not to say
            </label>
          </div>
        </div>

        <div className="input-group file-upload">
          <h3>Profile Picture (Optional)</h3>
          <input
            type="file"
            name="profilePicture"
            accept="image/*"
            onChange={handleFileChange}
            disabled={isLoading}
          />
        </div>

        <button 
          type="submit" 
          disabled={!isPasswordValid() || isLoading}
          className={isLoading ? "loading" : ""}
        >
          {isLoading ? "Creating Account..." : "Create Account"}
        </button>
      </form>
    </div>
  );
};

export default Signup;